import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEnvironment } from '../context/EnvironmentContext';

/**
 * Custom hook for feature analytics pages.
 *
 * Provides consistent data fetching, loading states, error handling,
 * and URL-based filter management for all feature analytics pages.
 *
 * @param {Function} fetchFn - API function to call (receives params object)
 * @param {Object} options - Optional configuration
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 5 minutes)
 * @param {boolean} options.autoRefresh - Enable auto-refresh (default: true). Set to false for testing/debugging.
 * @returns {Object} Analytics state and handlers
 *
 * @example
 * const {
 *   data, loading, error, lastUpdated, fetchData,
 *   filters, handleFilterChange
 * } = useFeatureAnalytics(founderDashboardAPI.getPrayerAnalytics);
 *
 * @example
 * // Disable auto-refresh for testing
 * const { data } = useFeatureAnalytics(fetchFn, { autoRefresh: false });
 */
export function useFeatureAnalytics(fetchFn, options = {}) {
  const { refreshInterval = 5 * 60 * 1000, autoRefresh = true } = options;
  const { currentEnv } = useEnvironment();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Extract filters from URL search params
  const organizationId = searchParams.get('organizationId');
  const campusId = searchParams.get('campusId');
  const groupId = searchParams.get('groupId');

  const filters = { organizationId, campusId, groupId };

  // Fetch data with current filters
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (organizationId) params.organizationId = organizationId;
      if (campusId) params.campusId = campusId;
      if (groupId) params.groupId = groupId;

      const response = await fetchFn(params);
      setData(response.data);
      setDataEnv(currentEnv);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchFn, organizationId, campusId, groupId, currentEnv]);

  // Fetch on mount and when filters/env change, with optional auto-refresh
  useEffect(() => {
    setData(null);
    setError(null);
    fetchData();

    if (!autoRefresh) return;

    const interval = setInterval(fetchData, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval, autoRefresh]);

  // Handle filter changes by updating URL search params
  const handleFilterChange = useCallback(({ organizationId, campusId, groupId }) => {
    const params = new URLSearchParams();
    if (organizationId) params.set('organizationId', organizationId);
    if (campusId) params.set('campusId', campusId);
    if (groupId) params.set('groupId', groupId);
    setSearchParams(params);
  }, [setSearchParams]);

  // Helper to check if error should be shown
  const showError = error && dataEnv === currentEnv;

  return {
    // State
    data,
    loading,
    error,
    showError,
    lastUpdated,
    dataEnv,
    currentEnv,

    // Filters
    filters,
    organizationId,
    campusId,
    groupId,

    // Actions
    fetchData,
    handleFilterChange
  };
}
